// src/components/gps/GPSManagement.js
import React, { useState, useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, Table, Badge, Button, Modal, FormField, Input, Select, SearchInput, ConfirmDialog, ResponsiveGrid } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import {
  TraccarConfig, loadCredentialsFromBackend,
  createTraccarDevice, deleteTraccarDevice, fetchTraccarDevices, fetchPositions,
} from '../../services/traccar';

const CONNECTIVITY_POLL_MS = 30000; // refresh online/offline status every 30s

// Format a timestamp as a short relative "time ago" string
function timeAgo(iso) {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const GPS_MODELS = [
  'Concox GT06N', 'Concox AT4', 'Concox GT100',
  'Teltonika FMB920', 'Teltonika FMB140', 'Teltonika FMC130',
  'Queclink GV300', 'Queclink GV500', 'Queclink GV600',
  'CalAmp LMU-2030', 'Meitrack T366G', 'Other',
];

const NETWORK_PROVIDERS = ['Smart', 'Globe', 'TNT', 'DITO', 'Other'];

const MODEL_PROTOCOLS = {
  'Concox GT06N': 'gt06 · port 5015',
  'Concox AT4':   'gt06 · port 5015',
  'Concox GT100': 'gt06 · port 5015',
  'Teltonika FMB920': 'teltonika · port 5027',
  'Teltonika FMB140': 'teltonika · port 5027',
  'Teltonika FMC130': 'teltonika · port 5027',
  'Queclink GV300': 'gl200 · port 5023',
  'Queclink GV500': 'gl200 · port 5023',
  'Queclink GV600': 'gl200 · port 5023',
};

export default function GPSManagement() {
  const { gpsDevices, addGPSDevice, updateGPSDevice, deleteGPSDevice, trucks, showNotification } = useApp();
  const { isMobile } = useResponsive();
  const [search, setSearch]           = useState('');
  const [modalOpen, setModalOpen]     = useState(false);
  const [editDevice, setEditDevice]   = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [traccarConnected, setTraccarConnected] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [connectivity, setConnectivity] = useState({}); // traccarDeviceId -> { online, lastUpdate }
  const [liveBattery, setLiveBattery] = useState({}); // traccarDeviceId -> battery % reported by the device itself, when available
  const [form, setForm] = useState({
    deviceId: '', model: GPS_MODELS[0], simCard: '', networkProvider: NETWORK_PROVIDERS[0], firmware: '', imei: '', traccarDeviceId: null, batteryLevel: null,
  });

  // GPS server connection is managed on the Settings page now — this just
  // checks whether it's currently configured, so the Add Device form knows
  // whether to auto-register a new device. Bootstraps from the shared
  // backend config first, so this works consistently for any admin/
  // encoder browser without needing to have run the "Connect" flow
  // locally — falls back to local storage automatically if that fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadCredentialsFromBackend();
      if (!cancelled) setTraccarConnected(TraccarConfig.isConfigured());
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll real online/offline status + last-ping time from the GPS server
  useEffect(() => {
    if (!traccarConnected) return;

    let cancelled = false;
    async function refresh() {
      try {
        const devices = await fetchTraccarDevices();
        if (cancelled) return;
        const map = {};
        devices.forEach(d => {
          map[d.id] = { online: d.status === 'online', lastUpdate: d.lastUpdate || null };
        });
        setConnectivity(map);

        // Many GPS trackers report their own battery level in their
        // position telemetry — pull the latest position for every
        // registered device and use that as a "live" reading wherever
        // it's genuinely available, so admins don't have to enter it
        // manually for devices that already tell us.
        //
        // IMPORTANT: only attrs.batteryLevel is trustworthy as a real
        // percentage — that's Traccar's own normalized 0-100% field.
        // attrs.battery is a DIFFERENT metric (usually raw battery
        // VOLTAGE, sometimes a fixed placeholder on hardwired trackers
        // with no real battery sensor) and must not be shown as if it
        // were a percentage.
        //
        // Some protocols (confirmed: "huabao", used by several common
        // GT06-family clone trackers) don't reliably decode a numeric
        // batteryLevel at all — the actual low-battery signal only comes
        // through as a discrete alarm flag instead. Getting a true
        // percentage for those devices requires configuring a "Computed
        // Attribute" directly on the Traccar server (mapping a raw,
        // device-specific I/O field — that's server-side Traccar admin
        // work, not something this app's code can add on its own). Until
        // that's set up, use the alarm flag as the best available signal.
        const positions = await fetchPositions(devices.map(d => d.id));
        if (cancelled) return;
        const battMap = {};
        positions.forEach(pos => {
          const attrs = pos.attributes || {};
          if (typeof attrs.batteryLevel === 'number') {
            battMap[pos.deviceId] = { level: Math.round(attrs.batteryLevel), lowAlarm: false };
          } else if (typeof attrs.alarm === 'string' && attrs.alarm.toLowerCase().includes('battery')) {
            battMap[pos.deviceId] = { level: null, lowAlarm: true };
          }
          // Deliberately NOT falling back to attrs.battery — see note above.
        });
        setLiveBattery(battMap);
      } catch {
        // Silent — connectivity/battery indicators just won't update this cycle
      }
    }

    refresh();
    const interval = setInterval(refresh, CONNECTIVITY_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [traccarConnected]);

  const filtered = gpsDevices.filter(g =>
    g.deviceId?.toLowerCase().includes(search.toLowerCase()) ||
    g.model?.toLowerCase().includes(search.toLowerCase()) ||
    g.imei?.includes(search) ||
    g.simCard?.includes(search) ||
    g.networkProvider?.toLowerCase().includes(search.toLowerCase())
  );

  function openAdd() {
    setEditDevice(null);
    setForm({ deviceId: '', model: GPS_MODELS[0], simCard: '', networkProvider: NETWORK_PROVIDERS[0], firmware: '', imei: '', traccarDeviceId: null, batteryLevel: null });
    setModalOpen(true);
  }

  function openEdit(device) {
    setEditDevice(device);
    setForm({
      deviceId: device.deviceId, model: device.model, simCard: device.simCard || '',
      networkProvider: device.networkProvider || NETWORK_PROVIDERS[0],
      firmware: device.firmware || '', imei: device.imei || '',
      traccarDeviceId: device.traccarDeviceId || null, batteryLevel: device.batteryLevel ?? null,
    });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.deviceId) return;
    setSaving(true);
    let traccarDeviceId = form.traccarDeviceId;

    // Auto-register in Traccar if connected, IMEI provided, and not already registered
    if (traccarConnected && form.imei && !editDevice?.traccarDeviceId) {
      try {
        const created = await createTraccarDevice(form.deviceId, form.imei);
        traccarDeviceId = created.id;
        showNotification(`Registered on GPS server (ID: ${created.id})`);
      } catch (e) {
        showNotification('GPS server registration failed: ' + e.message, 'error');
      }
    }

    const deviceData = { ...form, traccarDeviceId };
    if (editDevice) {
      updateGPSDevice(editDevice.id, deviceData);
    } else {
      addGPSDevice(deviceData);
    }
    setSaving(false);
    setModalOpen(false);
  }

  async function handleDelete(device) {
    if (traccarConnected && device.traccarDeviceId) {
      try {
        await deleteTraccarDevice(device.traccarDeviceId);
        showNotification('Removed from GPS server');
      } catch (e) {
        showNotification('Could not remove from GPS server: ' + e.message, 'error');
      }
    }
    await deleteGPSDevice(device.id);
  }

  const statusColors = { assigned: '#10b981', available: '#3b82f6', maintenance: '#f59e0b' };
  const protocol = MODEL_PROTOCOLS[form.model];

  const columns = [
    {
      key: 'deviceId', label: 'Device',
      render: (val, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(139,92,246,0.12)', color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>📡</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--textPrimary)', fontFamily: 'Syne, sans-serif' }}>{val}</div>
            <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{row.model}</div>
          </div>
        </div>
      )
    },
    {
      key: 'imei', label: 'IMEI / GPS Server',
      render: (v, row) => (
        <div>
          <div style={{ fontSize: 12, color: 'var(--textSecondary)', fontFamily: 'monospace' }}>{v || '—'}</div>
          {row.traccarDeviceId
            ? <div style={{ fontSize: 10, color: '#10b981', marginTop: 2 }}>✓ GPS ID: {row.traccarDeviceId}</div>
            : <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2 }}>⚠ Not linked to GPS server</div>
          }
        </div>
      )
    },
    {
      key: 'simCard', label: 'SIM Card',
      render: (v, row) => (
        <div>
          <div style={{ color: 'var(--textSecondary)', fontSize: 12 }}>{v || '—'}</div>
          {row.networkProvider && <div style={{ color: 'var(--textFaint)', fontSize: 10, marginTop: 1 }}>{row.networkProvider}</div>}
        </div>
      )
    },
    { key: 'firmware', label: 'Firmware', render: v => <Badge color="#6b7280">{v || '—'}</Badge> },
    {
      key: 'status', label: 'Status',
      render: v => <Badge color={statusColors[v] || '#6b7280'}>{v}</Badge>
    },
    {
      key: 'assignedTruck', label: 'Assigned Truck',
      render: v => {
        const truck = trucks.find(t => t.id === v);
        return truck
          ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: truck.color }} />
              <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{truck.plateNumber}</span>
            </div>
          : <span style={{ fontSize: 12, color: 'var(--textFaint)' }}>—</span>;
      }
    },
    {
      key: 'lastPing', label: 'Connectivity',
      render: (v, row) => {
        // No GPS server link at all — nothing live to show
        if (!row.traccarDeviceId) {
          return <span style={{ fontSize: 11, color: 'var(--textFaint)' }}>— Not linked</span>;
        }
        const live = connectivity[row.traccarDeviceId];
        // Linked, but we haven't heard back from the server yet (still loading, or server offline)
        if (!traccarConnected || !live) {
          return <span style={{ fontSize: 11, color: 'var(--textFaint)' }}>{v || 'Awaiting sync...'}</span>;
        }
        const ago = timeAgo(live.lastUpdate);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
              background: live.online ? '#10b981' : '#6b7280',
              boxShadow: live.online ? '0 0 6px #10b981' : 'none',
            }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: live.online ? '#10b981' : 'var(--textMuted)' }}>
                {live.online ? 'Online' : 'Offline'}
              </div>
              {ago && <div style={{ fontSize: 10, color: 'var(--textFaint)' }}>{ago}</div>}
            </div>
          </div>
        );
      }
    },
    {
      key: 'batteryLevel', label: 'Battery',
      render: (v, row) => {
        const live = row.traccarDeviceId ? liveBattery[row.traccarDeviceId] : null;

        // The device reported a live low-battery ALARM but no numeric
        // percentage exists for it (common on the "huabao" protocol and
        // similar) — show that as a distinct warning rather than a
        // percentage we don't actually have.
        if (live?.lowAlarm) {
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 13 }}>⚠️</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444' }}>Low Battery</span>
              </div>
              <div style={{ fontSize: 9, color: 'var(--textFaint)', marginTop: 2 }}>Alarm (no % available)</div>
            </div>
          );
        }

        const level = live?.level ?? v;
        if (level == null) return <span style={{ fontSize: 11, color: 'var(--textFaint)' }}>—</span>;
        const color = level > 50 ? '#10b981' : level > 20 ? '#f59e0b' : '#ef4444';
        const icon = level > 50 ? '🔋' : level > 20 ? '🪫' : '🪫';
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13 }}>{icon}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color }}>{level}%</span>
            </div>
            <div style={{ width: 48, height: 4, background: 'var(--border)', borderRadius: 3, overflow: 'hidden', marginTop: 3 }}>
              <div style={{ width: `${level}%`, height: '100%', background: color, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 9, color: 'var(--textFaint)', marginTop: 2 }}>{live?.level != null ? 'Live' : 'Manual'}</div>
          </div>
        );
      }
    },
    {
      key: 'id', label: 'Actions',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); setConfirmDelete(row); }} disabled={row.status === 'assigned'}>Del</Button>
        </div>
      )
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {!traccarConnected && (
        <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: 'var(--textSecondary)' }}>
          ℹ GPS server connection has moved to <strong style={{ color: 'var(--textPrimary)' }}>Settings</strong>. Connect it there to auto-register new devices and import from the GPS server.
        </div>
      )}

      {/* ShipDoTrack GPS Devices Table */}
      <Card>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: isMobile ? 'stretch' : 'center', marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name, IMEI, model..." />
          </div>
          <Button onClick={openAdd} icon="＋">Add Device</Button>
        </div>

        <ResponsiveGrid columns={3} tabletColumns={2} mobileColumns={1} gap={12} style={{ marginBottom: traccarConnected ? 12 : 20 }}>
          {[
            { label: 'Assigned',    count: gpsDevices.filter(g => g.status === 'assigned').length,     color: '#10b981' },
            { label: 'Available',   count: gpsDevices.filter(g => g.status === 'available').length,    color: '#3b82f6' },
            { label: 'Maintenance', count: gpsDevices.filter(g => g.status === 'maintenance').length,  color: '#f59e0b' },
          ].map(item => (
            <div key={item.label} style={{ background: 'var(--bgInput)', border: `1px solid ${item.color}22`, borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32, color: item.color }}>{item.count}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 4 }}>{item.label}</div>
            </div>
          ))}
        </ResponsiveGrid>

        {/* Live connectivity summary — only meaningful once linked to the GPS server */}
        {traccarConnected && (
          <ResponsiveGrid columns={3} tabletColumns={3} mobileColumns={3} gap={10} style={{ marginBottom: 20 }}>
            {(() => {
              const linked = gpsDevices.filter(g => g.traccarDeviceId);
              const online = linked.filter(g => connectivity[g.traccarDeviceId]?.online).length;
              const offline = linked.filter(g => connectivity[g.traccarDeviceId] && !connectivity[g.traccarDeviceId].online).length;
              const notLinked = gpsDevices.length - linked.length;
              return [
                { label: '● Online',    count: online,    color: '#10b981' },
                { label: '● Offline',   count: offline,   color: '#6b7280' },
                { label: 'Not Linked',  count: notLinked, color: '#f59e0b' },
              ];
            })().map(item => (
              <div key={item.label} style={{ background: 'var(--bgInput)', border: `1px solid ${item.color}18`, borderRadius: 8, padding: '9px 14px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 18, color: item.color }}>{item.count}</div>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: 2 }}>{item.label}</div>
              </div>
            ))}
          </ResponsiveGrid>
        )}

        <Table columns={columns} data={filtered} emptyMessage="No GPS devices yet. Connect GPS server and click Import." />
      </Card>

      {/* Add / Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editDevice ? 'Edit GPS Device' : 'Add GPS Device'} width={540}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <FormField label="Device Name / Label" required>
              <Input value={form.deviceId} onChange={v => setForm(f => ({ ...f, deviceId: v }))} placeholder="e.g. Truck-01-GPS" required />
            </FormField>
            <FormField label="Device Model">
              <Select value={form.model} onChange={v => setForm(f => ({ ...f, model: v }))} options={GPS_MODELS.map(m => ({ value: m, label: m }))} />
            </FormField>
          </div>

          <FormField label="IMEI Number">
            <Input value={form.imei} onChange={v => setForm(f => ({ ...f, imei: v }))} placeholder="15-digit IMEI printed on your GPS device" />
          </FormField>

          {protocol && (
            <div style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
              <span style={{ color: '#f97316', fontWeight: 600 }}>📶 Protocol: </span>
              <span style={{ color: 'var(--textSecondary)' }}>{protocol} — configure your device to connect to </span>
              <span style={{ color: '#f97316', fontFamily: 'monospace' }}>187.77.137.252:{protocol.match(/\d{4}/)?.[0] || '5015'}</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <FormField label="SIM Card Number">
              <Input value={form.simCard} onChange={v => setForm(f => ({ ...f, simCard: v }))} placeholder="09XXXXXXXXX" />
            </FormField>
            <FormField label="Network Provider">
              <Select value={form.networkProvider} onChange={v => setForm(f => ({ ...f, networkProvider: v }))} options={NETWORK_PROVIDERS.map(n => ({ value: n, label: n }))} />
            </FormField>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <FormField label="Firmware Version">
              <Input value={form.firmware} onChange={v => setForm(f => ({ ...f, firmware: v }))} placeholder="e.g. v2.1" />
            </FormField>
          </div>

          <FormField
            label="Battery Level (Manual)"
            hint={(() => {
              const live = editDevice?.traccarDeviceId ? liveBattery[editDevice.traccarDeviceId] : null;
              if (live?.level != null) {
                return `This device reports its own battery live (currently ${live.level}%) — that live reading takes priority over this manual value.`;
              }
              if (live?.lowAlarm) {
                return 'This device is currently signaling a Low Battery alarm, but doesn\'t report an exact percentage — set your best estimate here manually.';
              }
              return 'Enter the current charge level after checking the device — used for devices that don\'t report battery automatically.';
            })()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="range" min="0" max="100" step="1"
                value={form.batteryLevel ?? 100}
                onChange={e => setForm(f => ({ ...f, batteryLevel: parseInt(e.target.value) }))}
                style={{ flex: 1, accentColor: '#f97316' }}
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--textPrimary)', minWidth: 40, textAlign: 'right' }}>
                {form.batteryLevel ?? 100}%
              </span>
            </div>
          </FormField>

          {traccarConnected && !form.traccarDeviceId && form.imei && (
            <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--textSecondary)' }}>
              ✅ <strong style={{ color: '#34d399' }}>GPS server connected</strong> — this device will be auto-registered on the GPS server when saved.
            </div>
          )}
          {form.traccarDeviceId && (
            <div style={{ background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ fontSize: 10, color: 'var(--textFaint)', textTransform: 'uppercase', marginBottom: 4 }}>GPS Server ID</div>
              <div style={{ fontSize: 13, color: '#10b981', fontFamily: 'monospace' }}>{form.traccarDeviceId}</div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Saving...' : editDevice ? 'Save Changes' : 'Add Device'}
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => handleDelete(confirmDelete)}
        title="Delete GPS Device"
        message={`Delete "${confirmDelete?.deviceId}"?${confirmDelete?.traccarDeviceId ? ' This will also remove it from Traccar.' : ''}`}
      />
    </div>
  );
}
